<template>
  <!-- Адрес и телефон — самостоятельные строки контактов приёмной комиссии. -->
  <v-card class="admission-addresses" elevation="0">
    <v-card-title class="admission-addresses__header">
      <div>
        <h2 class="admission-addresses__title">Адреса и телефоны приёмной комиссии</h2>
        <p class="admission-addresses__description">
          Укажите места приёма документов и контактные телефоны.
        </p>
      </div>

      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        :disabled="loading"
        @click="openEditor()"
      >
        Добавить
      </v-btn>
    </v-card-title>

    <v-card-text class="admission-addresses__content">
      <v-alert
        v-if="error"
        class="admission-addresses__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader v-for="index in 3" :key="index" type="list-item-two-line" />
      </div>

      <div v-else-if="!items.length" class="admission-addresses__empty">
        <v-icon icon="mdi-map-marker-plus-outline" size="42" />
        <div>
          <strong>Адреса пока не добавлены</strong>
          <span>Добавьте место работы приёмной комиссии и телефон для связи.</span>
        </div>
      </div>

      <div v-else class="admission-addresses__list">
        <article v-for="item in items" :key="item.id" class="admission-addresses__item">
          <div class="admission-addresses__item-icon">
            <v-icon icon="mdi-map-marker-outline" size="24" />
          </div>

          <div class="admission-addresses__item-content">
            <div class="admission-addresses__item-address">{{ item.name }}</div>
            <a class="admission-addresses__item-phone" :href="phoneHref(item.phone)">
              <v-icon icon="mdi-phone-outline" size="17" />
              {{ item.phone }}
            </a>
          </div>

          <div class="admission-addresses__item-actions">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              size="small"
              aria-label="Редактировать адрес"
              @click="openEditor(item)"
            />
            <v-btn
              icon="mdi-delete-outline"
              variant="text"
              color="error"
              size="small"
              aria-label="Удалить адрес"
              @click="openDeleteDialog(item)"
            />
          </div>
        </article>
      </div>
    </v-card-text>

    <v-dialog v-model="editorDialog" max-width="760" persistent>
      <v-card>
        <v-card-title>
          {{ editedItem.id ? 'Редактирование адреса' : 'Новый адрес' }}
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <v-textarea
            v-model.trim="editedItem.address"
            label="Адрес приёмной комиссии"
            variant="outlined"
            rows="3"
            auto-grow
            autocomplete="street-address"
            hide-details="auto"
            required
          />

          <v-text-field
            v-model.trim="editedItem.phone"
            class="mt-5"
            label="Контактный телефон"
            variant="outlined"
            prepend-inner-icon="mdi-phone-outline"
            autocomplete="tel"
            placeholder="+7 (___) ___-__-__"
            hide-details="auto"
            required
            @keyup.enter="saveItem"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="closeEditor">Отмена</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="saveItem">
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460" persistent>
      <v-card>
        <v-card-title>Удалить адрес?</v-card-title>
        <v-card-text>
          <p class="mb-2">Адрес будет удалён из настроек приёмной комиссии.</p>
          <strong>{{ selectedItem ? selectedItem.name : '' }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">
            Отмена
          </v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="deleteItem">
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'AddressSection',
  data() {
    return {
      items: [],
      loading: true,
      saving: false,
      deleting: false,
      error: '',
      dialogError: '',
      editorDialog: false,
      deleteDialog: false,
      selectedItem: null,
      editedItem: {
        id: 0,
        address: '',
        phone: ''
      }
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    emptyItem() {
      return {
        id: 0,
        address: '',
        phone: ''
      };
    },
    loadData() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getAdmissionAddresses'
        )
        .then((response) => {
          this.items = response.data.items || [];
        })
        .catch((error) => {
          this.error = 'Не удалось загрузить адреса приёмной комиссии.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openEditor(item = null) {
      this.dialogError = '';

      if (item) {
        this.editedItem = {
          id: Number(item.id),
          address: item.name || '',
          phone: item.phone || ''
        };
      } else {
        this.editedItem = this.emptyItem();
      }

      this.editorDialog = true;
    },
    closeEditor() {
      this.editorDialog = false;
      this.dialogError = '';
    },
    saveItem() {
      const address = (this.editedItem.address || '').trim();
      const phone = (this.editedItem.phone || '').trim();

      if (!address || !phone) {
        this.dialogError = 'Заполните адрес и контактный телефон.';
        return;
      }

      this.saving = true;
      this.dialogError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveAdmissionAddress',
          new URLSearchParams({
            id: String(this.editedItem.id || 0),
            name: address,
            phone
          })
        )
        .then((response) => {
          const savedItem = response.data.item;
          const index = this.items.findIndex((item) => Number(item.id) === Number(savedItem.id));

          if (index === -1) {
            this.items.push(savedItem);
          } else {
            this.items.splice(index, 1, savedItem);
          }

          this.closeEditor();
        })
        .catch((error) => {
          this.dialogError = 'Не удалось сохранить адрес.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openDeleteDialog(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },
    deleteItem() {
      if (!this.selectedItem) {
        return;
      }

      this.deleting = true;
      this.error = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/deleteAdmissionAddress',
          new URLSearchParams({ id: String(this.selectedItem.id) })
        )
        .then(() => {
          this.items = this.items.filter(
            (item) => Number(item.id) !== Number(this.selectedItem.id)
          );
          this.deleteDialog = false;
          this.selectedItem = null;
        })
        .catch((error) => {
          this.error = 'Не удалось удалить адрес.';
          this.deleteDialog = false;
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    phoneHref(phone) {
      return 'tel:' + String(phone || '').replace(/[^\d+]/g, '');
    }
  }
};
</script>

<style scoped>
.admission-addresses {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.admission-addresses__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.admission-addresses__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.admission-addresses__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.admission-addresses__content {
  padding: 24px 28px 28px;
}

.admission-addresses__alert {
  margin-bottom: 20px;
}

.admission-addresses__list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

.admission-addresses__item {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  min-height: 76px;
  padding: 12px 14px;
  border-bottom: 1px solid #edf0f4;
}

.admission-addresses__item:last-child {
  border-bottom: 0;
}

.admission-addresses__item-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 10px;
  background: #eaf3fc;
  color: #1565c0;
}

.admission-addresses__item-address {
  color: #344054;
  font-weight: 500;
}

.admission-addresses__item-phone {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
  color: #1565c0;
  font-size: 14px;
  text-decoration: none;
}

.admission-addresses__item-actions {
  display: flex;
}

.admission-addresses__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 190px;
  color: #667085;
}

.admission-addresses__empty strong,
.admission-addresses__empty span {
  display: block;
}

.admission-addresses__empty strong {
  margin-bottom: 4px;
  color: #344054;
}

@media (max-width: 600px) {
  .admission-addresses__header {
    align-items: stretch;
    flex-direction: column;
  }

  .admission-addresses__header,
  .admission-addresses__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .admission-addresses__item {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .admission-addresses__item-icon {
    display: none;
  }
}
</style>
